import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react"
import { useApiReadEntireCollection } from "source/shared/SessionApiResource"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import PropTypes from "prop-types"
import { isEmpty, uniqBy } from "lodash"
import Header from "./Header"
import NameBanner from "./NameBanner"
import { Icon } from "source/shared/components"
import { ErrorMessages, Field, TextInput } from "./ui"
import {
  fetchAddressSuggestionDebounced,
  prepopulateValues,
  showFirstNameWarning,
  trackEmailSuggestionClick,
  uploadFileAttachment,
  buildIncludedParam,
  trimmedValues,
  extractAuthErrors,
  extractFieldErrors,
  optionallyLimitFieldErrors,
  fieldShouldBeHidden,
  clearConditionallyHiddenFields,
} from "./utils"
import Thanks from "./Thanks"
import { componentForFieldType } from "./FieldComponents"
import RecaptchaForm from "./RecaptchaForm"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { WebBootContext } from "source/publishing/WebBoot"

const TOTAL_FILE_SIZE_LIMIT = 1024 * 1024 * 10 * 5

function useAllFormFields(formId) {
  const responses = useApiReadEntireCollection(
    `/people/v2/forms/${formId}/fields?include=options,field_definitions,field_options,marital_statuses,campuses,form_field_conditions,school_options,genders&order=sequence&fields[FieldOption]=label,value2,sequence&per_page=100`,
  )

  return useMemo(() => {
    return responses.reduce(
      (acc, { data, included }) => {
        return {
          data: [...acc.data, ...data],
          included: uniqBy([...acc.included, ...included], "id"),
        }
      },
      { data: [], included: [] },
    )
  }, [responses])
}

function Form({
  id,
  name,
  description,
  defaultCountryCode,
  defaultEmailAddress,
  encryptedParam,
  grades,
  phoneTypes,
  addressTypes,
  sendSubmissionNotificationToSubmitter,
  submittedByEmailAddress,
  recaptchaToken,
  wrapSubmit,
  setRecaptchaLoading,
  ...props
}) {
  const [person, setPerson] = useState(props.person)
  const [loading, setLoading] = useState(true)
  const [loggedInPerson, setLoggedInPerson] = useState(null)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [emailAddress, setEmailAddress] = useState(defaultEmailAddress ?? "")
  const [suggestedEmailAddress, setSuggestedEmailAddress] = useState(null)
  const [values, setValues] = useState({})
  const [countries, setCountries] = useState([])
  const [attachments, setAttachments] = useState([])
  const [errors, setErrors] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const [verificationRequired, setVerificationRequired] = useState(false)
  const [submissionConfirmation, setSubmissionConfirmation] = useState({})
  const [submitDisabled, setSubmitDisabled] = useState(false)

  const recaptchaRef = useRef(recaptchaToken)
  useEffect(() => {
    recaptchaRef.current = recaptchaToken
  }, [recaptchaToken])

  const headerRef = useRef()

  const handleAddressChange = (e) => {
    fetchAddressSuggestionDebounced(e.target.value, (newState) =>
      setSuggestedEmailAddress(newState),
    )

    setSuggestedEmailAddress(undefined)

    setEmailAddress(e.target.value)
  }

  const handleEmailSuggestionClick = () => {
    setEmailAddress(() => suggestedEmailAddress)
    setSuggestedEmailAddress(() => undefined)
    trackEmailSuggestionClick(emailAddress, suggestedEmailAddress)
  }

  const fieldData = useAllFormFields(id)
  const fields = useMemo(() => fieldData.data || [], [fieldData.data])
  const included = useMemo(() => fieldData.included || [], [fieldData.included])

  const hasInitializedValues = useRef(false)

  useEffect(() => {
    if (fieldData.data?.length > 0 && !hasInitializedValues.current) {
      setValues((prevValues) => {
        if (Object.keys(prevValues).length === 0) {
          hasInitializedValues.current = true
          return prepopulateValues(fieldData.data, defaultCountryCode)
        }
        return prevValues
      })
      const hasAddressField = fieldData.data.some(
        (field) => field?.attributes?.field_type === "address",
      )
      if (hasAddressField && (!countries || countries.length === 0)) {
        sessionApiClient.get("/people/v2/countries").then((response) => {
          setCountries(response.data)
        })
      }
    }
  }, [defaultCountryCode, fieldData.data, countries])

  const clearAndCleanupFields = React.useCallback(
    (values) => {
      let newValues = { ...values }

      const fieldsToRemove = clearConditionallyHiddenFields({
        fields,
        included,
        values: newValues,
      })

      fieldsToRemove.forEach((fieldId) => {
        if (newValues[fieldId]) {
          delete newValues[fieldId]
        }
      })

      return newValues
    },
    [fields, included],
  )

  // Ensure address fields get proper default values when they become visible
  const ensureAddressFieldDefaults = React.useCallback(
    (updatedValues) => {
      if (fields.length > 0 && defaultCountryCode) {
        const newValues = { ...updatedValues }
        let hasChanges = false

        fields.forEach((field) => {
          if (field?.attributes?.field_type === "address") {
            const fieldId = field.id
            const isVisible = !fieldShouldBeHidden({
              field,
              fields,
              included,
              values: updatedValues,
            })

            if (isVisible) {
              const currentValue = updatedValues[fieldId]
              // If field is visible but has no value or incomplete value, ensure it has country code
              if (!currentValue || !currentValue.country_code) {
                newValues[fieldId] = {
                  ...currentValue,
                  country_code: defaultCountryCode,
                }
                hasChanges = true
              }
            } else {
              // Field is hidden, clear its value
              if (updatedValues[fieldId]) {
                delete newValues[fieldId]
                hasChanges = true
              }
            }
          }
        })

        return hasChanges ? newValues : updatedValues
      }
      return updatedValues
    },
    [fields, included, defaultCountryCode],
  )

  useEffect(() => {
    if (!person && !loggedInPerson) {
      sessionApiClient
        .check()
        .then((browserSessionPerson) => {
          if (browserSessionPerson.data.type === "VerifiedInSessionPerson") {
            setLoggedInPerson(browserSessionPerson)
          }
          setLoading(false)
          setRecaptchaLoading?.(false)
        })
        .catch(() => {
          setLoading(false)
          setRecaptchaLoading?.(false)
        })
    } else {
      setLoading(false)
      setRecaptchaLoading?.(false)
    }
  }, [person, loggedInPerson, setRecaptchaLoading])

  const showTypoSuggestion =
    suggestedEmailAddress && emailAddress !== suggestedEmailAddress

  const handleValueChange = useCallback(
    (value, id) => {
      setValues((prevValues) => {
        let newValues = { ...prevValues, [id]: value }
        if (isEmpty(newValues[id]) && newValues[id] !== true) {
          delete newValues[id]
        }

        newValues = clearAndCleanupFields(newValues)
        newValues = ensureAddressFieldDefaults(newValues)

        return newValues
      })
    },
    [clearAndCleanupFields, ensureAddressFieldDefaults],
  )

  const handleMergedValueChange = React.useCallback(
    (obj, id) => {
      if (obj === null) {
        setValues((prevValues) => {
          let newValues = { ...prevValues }
          delete newValues[id]

          newValues = clearAndCleanupFields(newValues)
          newValues = ensureAddressFieldDefaults(newValues)

          return newValues
        })
        return
      }
      setValues((prevValues) => {
        const prev = prevValues[id] || {}
        const next = { ...prev, ...obj }

        const isEqual = Object.entries(obj).every(
          ([key, value]) => prev[key] === value,
        )

        if (isEqual) {
          return prevValues
        }

        let newValues = { ...prevValues, [id]: next }

        newValues = clearAndCleanupFields(newValues)
        newValues = ensureAddressFieldDefaults(newValues)

        return newValues
      })
    },
    [clearAndCleanupFields, ensureAddressFieldDefaults],
  )

  const attachmentsForField = (id) => {
    const attachements = attachments.find((att) => att.form_field_id === id)
    return attachements ? attachements["files"] : []
  }

  const handleAddAttachment = (attachment) => {
    if (attachment) {
      const newAttachments = attachments.filter(
        (att) => att.form_field_id !== attachment.form_field_id,
      )
      setAttachments([...newAttachments, attachment])
      handleValueChange("attachments", attachment.form_field_id)
    }
  }

  const handleRemoveAttachment = (formFieldId, fileName) => {
    const formAttachments =
      attachments?.filter((at) => at.form_field_id !== formFieldId) || []

    const filesForFormField = attachmentsForField(formFieldId).filter(
      (att) => att.name !== fileName,
    )

    const newAttachments =
      filesForFormField.length > 0
        ? [
            ...formAttachments,
            { form_field_id: formFieldId, files: filesForFormField },
          ]
        : formAttachments

    setAttachments(newAttachments)

    if (filesForFormField.length === 0) {
      handleValueChange("", formFieldId)
    }
  }

  const totalFileSizeExceedsLimit = () => {
    return (
      attachments
        .flatMap((attachment) => attachment.files)
        .reduce((acc, currentFile) => acc + currentFile.size, 0) >
      TOTAL_FILE_SIZE_LIMIT
    )
  }
  const handleNumberValueChange = React.useCallback((value, id) => {
    const parsedValue = parseFloat(value)
    const nextValue = isNaN(parsedValue) ? "" : parsedValue

    setValues((prevValues) => ({
      ...prevValues,
      [id]: nextValue,
    }))
  }, [])

  const handleSubmit = async (person) => {
    if (submitDisabled) return

    setSubmitDisabled(true)

    const recaptchaToken = recaptchaRef.current
    const validAttachments = attachments.filter(
      (a) => a && a.files && Array.isArray(a.files),
    )
    const uploads = validAttachments.map((a) => uploadFileAttachment(a))
    const uploadedAttachments = await Promise.all(uploads)

    const included = buildIncludedParam(
      trimmedValues(values),
      uploadedAttachments,
      fields,
    )

    let attributes
    if (person.id) {
      attributes = {
        person_id: person.id,
        email_address: person.emailAddress,
        _recaptcha_token: recaptchaToken,
      }
      if (encryptedParam) {
        attributes["_e"] = encryptedParam
      }
    } else {
      attributes = {
        _recaptcha_token: recaptchaToken,
        email_address: person.emailAddress,
        person_attributes: {
          first_name: person.firstName,
          last_name: person.lastName,
          emails_attributes: [
            { location: "Home", address: person.emailAddress },
          ],
        },
      }
    }

    sessionApiClient
      .post(`/people/v2/forms/${id}/form_submissions`, {
        data: {
          type: "FormSubmission",
          attributes,
        },
        included,
      })
      .then((resp) => {
        setVerificationRequired(resp.meta.requires_verification)
        setSubmissionConfirmation({
          basicInformation: resp.meta.basic_information,
          groupedSubmissionValues: resp.meta.grouped_submission_values,
        })
        setSubmitted(true)
        setSubmitDisabled(false)
      })
      .catch((resp) => {
        const respErrors = Array.isArray(resp.errors) ? resp.errors : []
        const formSubmissionErrors = respErrors.find(
          (e) => e.source && e.source.parameter === "form_submission_values",
        )

        const errors = {
          authErrors: extractAuthErrors(respErrors),
          fieldErrors: extractFieldErrors(formSubmissionErrors),
        }

        if (errors.fieldErrors) {
          optionallyLimitFieldErrors(fields, errors.fieldErrors)
        }

        if (
          !errors.fieldErrors &&
          (!errors.authErrors || isEmpty(errors.authErrors))
        ) {
          errors.formErrors = respErrors[0]?.detail || "An error occurred"
        }

        if (!isEmpty(errors.authErrors)) {
          setErrors(errors)
          setPerson(null)
          setLoggedInPerson(null)
        } else {
          setErrors(errors)
        }

        setSubmitDisabled(false)

        if (headerRef.current) {
          setTimeout(() =>
            headerRef.current.scrollIntoView({ behavior: "smooth" }),
          )
        }
      })
  }

  const authErrors = errors.authErrors || {}

  return submitted ? (
    <Thanks
      attachments={attachments}
      verificationRequired={verificationRequired}
      sendSubmissionNotificationToSubmitter={
        sendSubmissionNotificationToSubmitter
      }
      submittedByEmailAddress={submittedByEmailAddress}
      submissionConfirmation={submissionConfirmation}
      onClick={() => {
        setValues(prepopulateValues(fields, defaultCountryCode))
        setErrors([])
        setSubmissionConfirmation({})
        setVerificationRequired(false)
        setFirstName("")
        setLastName("")
        setEmailAddress(person ? person.data?.attributes?.email_address : "")
        setSubmitted(false)
        setLoading(false)
      }}
    />
  ) : (
    <div ref={headerRef}>
      {!loading && (
        <>
          <Header name={name} description={description}>
            {(!isEmpty(errors.fieldErrors) || !isEmpty(errors.authErrors)) && (
              <div className="my-2">
                <div className="quiet-alert error-alert alert d-f ai-c mb-2">
                  <span className="icon c-ruby mr-1 fs-3">
                    <Icon symbol="general#exclamation-triangle" aria-hidden />
                  </span>
                  <strong className="ta-l c-ruby">
                    Uh oh! Looks like there were some problems.
                  </strong>
                </div>
              </div>
            )}
          </Header>
          <form onSubmit={(e) => e.preventDefault()}>
            <section>
              <div>
                {loggedInPerson ? (
                  <NameBanner person={loggedInPerson} info="My information" />
                ) : person ? (
                  <NameBanner person={person} info="Submitting form for" />
                ) : (
                  <div id="new-person-form">
                    <Field
                      id="1"
                      field_type="your_name"
                      label="Your name"
                      required
                    >
                      <div className="d-f@md f_1">
                        <div>
                          <TextInput
                            errors={authErrors["first name"]}
                            id="your_name_1"
                            placeholder="First name"
                            autoComplete="given-name"
                            value={firstName}
                            spellCheck={false}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                          />
                          <ErrorMessages
                            errors={authErrors["first name"]}
                            className="mt-1"
                          />
                          {showFirstNameWarning(firstName) && (
                            <div className="c-marigold mt-1">
                              For accuracy, use one name only.
                            </div>
                          )}
                        </div>
                        <div className="ml-0 ml-1@md mt-1 mt-0@md">
                          <label
                            className="screen-reader-text"
                            htmlFor="last_name"
                          >
                            Last name
                          </label>
                          <TextInput
                            id="last_name"
                            errors={authErrors["last name"]}
                            placeholder="Last name"
                            autoComplete="family-name"
                            value={lastName}
                            spellCheck={false}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                          />
                          <ErrorMessages
                            errors={authErrors["last name"]}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </Field>
                    <Field
                      id="1"
                      errors={authErrors["email address"]}
                      field_type="email_address"
                      label="Email address"
                      required
                    >
                      <TextInput
                        errors={authErrors["email address"]}
                        type="email"
                        id="email_address_1"
                        placeholder="name@example.com"
                        autoComplete="email"
                        value={emailAddress}
                        onChange={handleAddressChange}
                        required
                        {...(showTypoSuggestion && {
                          style: {
                            boxShadow: "none",
                            outline: "none",
                            borderColor: "var(--color-citrine)",
                          },
                        })}
                      />
                      {showTypoSuggestion && (
                        <div
                          className="fs-4 mt-2p"
                          style={{
                            color: "var(--warning-alert--text)",
                          }}
                        >
                          Did you mean <strong>{suggestedEmailAddress}</strong>?{" "}
                          <button
                            className="btn sm-btn text-btn fs-5 h-2 td-u:h"
                            onClick={handleEmailSuggestionClick}
                          >
                            Replace
                          </button>
                        </div>
                      )}
                    </Field>
                  </div>
                )}
                {fields.map((field) => {
                  if (
                    fieldShouldBeHidden({ field, fields, included, values })
                  ) {
                    return null
                  }
                  return componentForFieldType(field, {
                    included,
                    grades,
                    handleValueChange,
                    handleMergedValueChange,
                    handleAddAttachment,
                    handleRemoveAttachment,
                    totalFileSizeExceedsLimit,
                    maxTotalFileSize: TOTAL_FILE_SIZE_LIMIT,
                    handleNumberValueChange,
                    values,
                    phoneTypes,
                    countries,
                    addressTypes,
                    errors,
                    personName:
                      loggedInPerson?.data?.attributes?.name ||
                      person?.data?.attributes?.name ||
                      (firstName && lastName
                        ? `${firstName} ${lastName}`.trim()
                        : null),
                  })
                })}
                <div className="my-4 ta-c">
                  <button
                    className="btn"
                    type="submit"
                    onClick={() =>
                      wrapSubmit(handleSubmit)({
                        id: loggedInPerson
                          ? loggedInPerson.data.id
                          : person && person.data.id,
                        firstName,
                        lastName,
                        emailAddress: loggedInPerson
                          ? loggedInPerson.data?.attributes?.email_address
                          : emailAddress
                            ? emailAddress
                            : person?.data?.attributes?.email_address,
                      })
                    }
                    disabled={submitDisabled}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </section>
            {errors.formErrors && (
              <p className="ta-c mt-1 c-ruby">{errors.formErrors}</p>
            )}
          </form>
        </>
      )}
    </div>
  )
}

Form.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  defaultCountryCode: PropTypes.string,
  defaultEmailAddress: PropTypes.string,
  encryptedParam: PropTypes.string,
  person: PropTypes.object,
  grades: PropTypes.array,
  phoneTypes: PropTypes.array,
  addressTypes: PropTypes.array,
  sendSubmissionNotificationToSubmitter: PropTypes.bool,
  submittedByEmailAddress: PropTypes.string,
  recaptchaToken: PropTypes.string,
  wrapSubmit: PropTypes.func.isRequired,
  setRecaptchaLoading: PropTypes.func,
}

export default function RecaptchaWrappedForm(props) {
  const {
    people: { recaptcha_site_key },
  } = useContext(ServerRenderedProps)

  const { features } = useContext(WebBootContext)

  return (
    <div className="recaptcha-form">
      <RecaptchaForm
        {...props}
        recaptchaSiteKey={recaptcha_site_key}
        recaptchaEnabled={!features.TOGGLE_disable_people_forms_recaptcha}
      >
        <Form {...props} />
      </RecaptchaForm>
    </div>
  )
}
